import { useCallback, useEffect, useState } from 'react'

export type ToteLabelSize = 'small' | 'large'

/** Physical dimensions (inches) for each tote label size. */
export const TOTE_LABEL_DIMENSIONS: Record<ToteLabelSize, { widthIn: number; heightIn: number }> = {
  small: { widthIn: 2.25, heightIn: 1.25 },
  large: { widthIn: 4, heightIn: 2 },
}

const STORAGE_KEY = 'toteLabel.size'
const EVENT = 'tote-label-size-change'

export function getToteLabelSize(): ToteLabelSize {
  const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null
  return raw === 'large' ? 'large' : 'small'
}

export function setToteLabelSize(size: ToteLabelSize): void {
  window.localStorage.setItem(STORAGE_KEY, size)
  window.dispatchEvent(new CustomEvent(EVENT))
}

/** Reactive accessor for the tote label size, synced across components and tabs. */
export function useToteLabelSize(): [ToteLabelSize, (size: ToteLabelSize) => void] {
  const [size, setSize] = useState<ToteLabelSize>(getToteLabelSize)

  useEffect(() => {
    const sync = () => setSize(getToteLabelSize())
    window.addEventListener(EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const update = useCallback((next: ToteLabelSize) => setToteLabelSize(next), [])
  return [size, update]
}
